// components/workflow/BookingConfirmation.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip
} from '@mui/material';
import { 
  Send as SendIcon, 
  Email as EmailIcon, 
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { BookingDetails, sendBookingConfirmationEmail, generateBookingId } from '../utils/emailService';

interface BookingConfirmationProps {
  bookingData: BookingDetails;
  onClose: () => void;
  onSuccess?: (bookingId: string) => void;
}

type EmailStatus = 'idle' | 'sending' | 'success' | 'error' | 'copied';

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  bookingData,
  onClose,
  onSuccess
}) => {
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [bookingId, setBookingId] = useState<string>(() => generateBookingId());

  const handleSendEmail = async (): Promise<void> => {
    setEmailStatus('sending');

    try {
      const bookingWithId: BookingDetails = {
        ...bookingData,
        bookingId
      };

      const success = sendBookingConfirmationEmail(bookingWithId);

      if (success) {
        setEmailStatus('success');
        
        // Save to localStorage for persistence
        localStorage.setItem(
          `booking_${bookingId}`, 
          JSON.stringify(bookingWithId)
        );

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(bookingId);
        }

        // Log for debugging
        console.log('Booking submitted:', bookingWithId);
      } else {
        throw new Error('Failed to open email client');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setEmailStatus('error');
    }
  };

  const handleCopyDetails = (): void => {
    const bookingText = `Booking Confirmation
────────────────
Service: ${bookingData.serviceType}
Date: ${bookingData.fromDate}
Time: ${bookingData.time}
Customer: ${bookingData.customerName}
Email: ${bookingData.customerEmail}
Phone: ${bookingData.customerPhone}
────────────────
Send to: help@nordisksupport.com
Booking ID: ${bookingId}`.trim();

    navigator.clipboard.writeText(bookingText)
      .then(() => {
        setEmailStatus('copied');
        setTimeout(() => setEmailStatus('idle'), 3000);
      })
      .catch(() => setEmailStatus('error'));
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pr: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon fontSize="large" />
          <Box>
            <Typography variant="h5">Booking Confirmation</Typography>
            <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
              Send confirmation to help@nordisksupport.com
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Booking ID */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={`Reference: ${bookingId}`} 
            color="primary" 
            size="small"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            Please keep this reference for future inquiries
          </Typography>
        </Box>

        {/* Booking Summary */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Booking Summary
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 3 
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Service Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.serviceType}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Date & Time
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(bookingData.fromDate)} at {bookingData.time}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Customer Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.customerName}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Contact Email
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.customerEmail}
              </Typography>
            </Box>

            {bookingData.address && (
              <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Service Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {bookingData.address}
                </Typography>
              </Box>
            )}
          </Box>

          {bookingData.notes && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Additional Notes
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                {bookingData.notes}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Instructions */}
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<EmailIcon />}
        >
          <Typography variant="body2">
            Clicking "Send Email" will open your default email client with a pre-filled email to{' '}
            <strong>help@nordisksupport.com</strong>. You just need to click "Send" in your email client.
          </Typography>
        </Alert>

        {/* Status Messages */}
        {emailStatus === 'success' && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            icon={<CheckCircleIcon />}
          >
            Email client opened! Please check your email application and click "Send" to complete the booking.
          </Alert>
        )}
        
        {emailStatus === 'copied' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Booking details copied to clipboard! You can paste them into an email manually.
          </Alert>
        )}
        
        {emailStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to open email client. Please try again or copy the details below.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        
        <Button 
          onClick={handleCopyDetails}
          startIcon={<DownloadIcon />}
          variant="outlined"
          disabled={emailStatus === 'sending'}
        >
          Copy Details
        </Button>
        
        <Button 
          onClick={handleSendEmail}
          variant="contained"
          startIcon={
            emailStatus === 'sending' ? 
              <CircularProgress size={20} color="inherit" /> : 
              <SendIcon />
          }
          disabled={emailStatus === 'sending'}
          sx={{ 
            minWidth: 200,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          {emailStatus === 'sending' ? 'Opening Email...' : 'Send Email Confirmation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingConfirmation;